// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Contact from "./contact.js"
import PhotoSessionGroupContact from "./photo-session-group-contact.js"

const modelClassData = {
  "attributes": {
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "photo_session_group_contact_id": {
      "column": null,
      "name": "photo_session_group_contact_id",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "PhotoSessionGroupContactTokenContact",
  "collectionKey": "photo_session_group_contact_token_contacts",
  "collectionName": "photo_session_group_contact_token_contacts",
  "i18nKey": "photo_session_group_contact_token_contact",
  "camelizedLower": "photoSessionGroupContactTokenContact",
  "name": "PhotoSessionGroupContactTokenContact",
  "nameDasherized": "photo-session-group-contact-token-contact",
  "pluralName": "photo_session_group_contact_token_contacts",
  "ransackable_associations": [
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "contact_id",
      "name": "contact",
      "macro": "belongs_to",
      "resource_name": "Contact",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "PhotoSessionGroupContact",
      "collectionName": "photo_session_group_contacts",
      "foreignKey": "photo_session_group_contact_id",
      "name": "photo_session_group_contact",
      "macro": "has_one",
      "resource_name": "PhotoSessionGroupContact",
      "through": "photo_session_group_contact_token"
    },
    {
      "className": "PhotoSessionGroupContactToken",
      "collectionName": "photo_session_group_contact_tokens",
      "foreignKey": "photo_session_group_contact_token_id",
      "name": "photo_session_group_contact_token",
      "macro": "belongs_to",
      "resource_name": "PhotoSessionGroupContactToken",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "contact_id",
      "column": {
        "default": null,
        "name": "contact_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "photo_session_group_contact_token_id",
      "column": {
        "default": null,
        "name": "photo_session_group_contact_token_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "Contact",
      "collectionName": "contacts",
      "foreignKey": "contact_id",
      "name": "contact",
      "macro": "belongs_to",
      "resource_name": "Contact",
      "through": null
    },
    {
      "className": "PhotoSessionGroupContact",
      "collectionName": "photo_session_group_contacts",
      "foreignKey": "photo_session_group_contact_id",
      "name": "photo_session_group_contact",
      "macro": "has_one",
      "resource_name": "PhotoSessionGroupContact",
      "through": "photo_session_group_contact_token"
    }
  ],
  "paramKey": "photo_session_group_contact_token_contact",
  "primaryKey": "id"
}

/** Frontend model for PhotoSessionGroupContactTokenContact. */
export default class PhotoSessionGroupContactTokenContact extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {any} */
  photoSessionGroupContactId() {
    return this.readAttributeUnderscore("photo_session_group_contact_id")
  }

  /** @returns {boolean} */
  hasPhotoSessionGroupContactId() {
    const value = this.photoSessionGroupContactId()

    return this._isPresent(value)
  }

  /** @returns {import("./contact.js").default | null} */
  contact() {
    return this._readBelongsToReflection({modelClass: Contact, reflectionName: "contact"})
  }

  /** @returns {Promise<import("./contact.js").default | null>} */
  loadContact() {
    if (!("contactId" in this)) throw new Error("Foreign key method wasn't defined: contactId")

    const id = this.contactId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "contact",
        model: this,
        modelClass: Contact
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./contact.js").default | null>} */
  ensureContactLoaded() {
    return this.ensureAssociationLoaded("contact")
  }

  /** @returns {import("./photo-session-group-contact.js").default | null} */
  photoSessionGroupContact() {
    return this._readHasOneReflection({modelClass: PhotoSessionGroupContact, reflectionName: "photo_session_group_contact"})
  }

  /** @returns {Promise<import("./photo-session-group-contact.js").default | null>} */
  loadPhotoSessionGroupContact() {
    if (!("id" in this)) throw new Error("Primary key method wasn't defined: id")

    const id = this.id()

    return this._loadHasOneReflection(
      {
        reflectionName: "photo_session_group_contact",
        model: this,
        modelClass: PhotoSessionGroupContact
      },
      {
        params: {
          through: {
            model: "PhotoSessionGroupContactTokenContact",
            id,
            reflection: "photo_session_group_contact"
          }
        }
      }
    )
  }

  /** @returns {Promise<import("./photo-session-group-contact.js").default | null>} */
  ensurePhotoSessionGroupContactLoaded() {
    return this.ensureAssociationLoaded("photo_session_group_contact")
  }
}
